import { useState, useEffect } from "react";
import { Switch, Route, Redirect, useLocation } from "wouter";
import { AuthProvider, useAuth } from "@/lib/authContext";
import { TimerProvider } from "@/lib/timerContext";
import { QuickCreateProvider } from "@/lib/quickCreateContext";
import { AIPageContextProvider } from "@/lib/aiPageContext";
import { ConfirmProvider } from "@/lib/confirmContext";
import { Toaster } from "@/components/ui/toaster";
import Today from "@/pages/Today";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import AuthCallback from "@/pages/AuthCallback";
import SetPassword from "@/pages/SetPassword";
import Settings from "@/pages/Settings";
import Reports from "@/pages/Reports";
import ThisPayPeriod from "@/pages/ThisPayPeriod";
import Submit from "@/pages/Submit";
import PTO from "@/pages/PTO";
import Review from "@/pages/Review";
import PayPeriodAdmin from "@/pages/PayPeriodAdmin";
import Invoices from "@/pages/Invoices";
import InvoiceEdit from "@/pages/InvoiceEdit";
import Accounts from "@/pages/Accounts";
import AccountDetail from "@/pages/AccountDetail";
import ClientResponses from "@/pages/ClientResponses";
import ClientEmailLog from "@/pages/ClientEmailLog";
import Inbox from "@/pages/Inbox";
import EmailSearch from "@/pages/EmailSearch";
import MyAccounts from "@/pages/MyAccounts";
import FinancialSources from "@/pages/FinancialSources";
import Projects from "@/pages/Projects";
import Contacts from "@/pages/Contacts";
import Templates from "@/pages/Templates";
import TemplateEdit from "@/pages/TemplateEdit";
import Tasks from "@/pages/Tasks";
import ClientGroups from "@/pages/ClientGroups";
import TagsPage from "@/pages/Tags";
import UsersPage from "@/pages/Users";
import AdminImport from "@/pages/AdminImport";
import AdminConfig from "@/pages/AdminConfig";
import InviteTeam from "@/pages/InviteTeam";
import FeedbackPage from "@/pages/Feedback";
import NotificationsPage from "@/pages/Notifications";
import TrashPage from "@/pages/Trash";
import AllTimesheets from "@/pages/AllTimesheets";
import ReportTime from "@/pages/ReportTime";
import ReportCapacity from "@/pages/ReportCapacity";
import CalendarPage from "@/pages/Calendar";
import SchedulingSettings from "@/pages/scheduling/SchedulingSettings";
import AutomationRules from "@/pages/AutomationRules";
import Messages from "@/pages/Messages";
import Signatures from "@/pages/Signatures";
import AgreementTemplates from "@/pages/AgreementTemplates";
import NotFound from "@/pages/not-found";
import UploadPage from "@/pages/UploadPage";
import PublicBookingPage from "@/pages/booking/PublicBookingPage";
import BookingManage from "@/pages/booking/BookingManage";
import ConnectBank from "@/pages/ConnectBank";
import PortalLogin from "@/pages/portal/PortalLogin";
import PortalCallback from "@/pages/portal/PortalCallback";
import PortalAccountPicker from "@/pages/portal/PortalAccountPicker";
import PortalDashboard from "@/pages/portal/PortalDashboard";
import PortalProject from "@/pages/portal/PortalProject";
import PortalSettings from "@/pages/portal/PortalSettings";
import PortalMessages from "@/pages/portal/PortalMessages";
import PortalHelp from "@/pages/portal/PortalHelp";
import { PortalAuthGuard } from "@/components/portal/PortalAuthGuard";
import { Card, CardContent } from "@/components/ui/card";
import { Layout } from "@/components/layout/Layout";
import { Loader2 } from "lucide-react";

const PlaceholderPage = ({ title }: { title: string }) => (
  <Layout>
    <div className="space-y-6">
      <h1 className="text-3xl font-heading font-bold">{title}</h1>
      <Card className="border-dashed border-2 bg-muted/20">
        <CardContent className="flex flex-col items-center justify-center h-64 text-muted-foreground">
          <p>This page is currently under construction.</p>
          <p className="text-sm mt-2">
            Navigate to <b>Today</b>, <b>Reports</b>, or <b>Settings</b> for the full demo.
          </p>
        </CardContent>
      </Card>
    </div>
  </Layout>
);

function AuthGuard({ children }: { children: React.ReactNode }) {
  const { user, loading, tryReauth } = useAuth();
  const [, setLocation] = useLocation();
  const [reauthing, setReauthing] = useState(false);

  useEffect(() => {
    if (!loading && !user && !reauthing) {
      setReauthing(true);
      tryReauth().then((ok) => {
        if (!ok) {
          const intended = window.location.pathname + window.location.search;
          if (intended.length > 1 && !intended.startsWith("/login") && !intended.startsWith("/set-password") && !intended.startsWith("/auth")) {
            sessionStorage.setItem("centcom_login_redirect", intended);
          }
          setLocation("/login");
        }
      }).finally(() => setReauthing(false));
    }
  }, [loading, user, reauthing, tryReauth, setLocation]);

  if (loading || reauthing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user) return null;

  return <>{children}</>;
}

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <Switch>
      {/* 🌐 Client portal routes (separate auth — no staff sidebar) */}
      <Route path="/portal/login">
        <PortalLogin />
      </Route>
      <Route path="/portal/callback">
        <PortalCallback />
      </Route>
      <Route path="/portal/pick-account">
        <PortalAuthGuard requireAccount={false}><PortalAccountPicker /></PortalAuthGuard>
      </Route>
      <Route path="/portal/project/:id">
        <PortalAuthGuard><PortalProject /></PortalAuthGuard>
      </Route>
      <Route path="/portal/messages">
        <PortalAuthGuard><PortalMessages /></PortalAuthGuard>
      </Route>
      <Route path="/portal/help">
        <PortalAuthGuard><PortalHelp /></PortalAuthGuard>
      </Route>
      <Route path="/portal/settings">
        <PortalAuthGuard><PortalSettings /></PortalAuthGuard>
      </Route>
      <Route path="/portal">
        <PortalAuthGuard><PortalDashboard /></PortalAuthGuard>
      </Route>

      {/* 🔓 Public routes */}
      <Route path="/book/:slug/:eventTypeSlug">
        <PublicBookingPage />
      </Route>
      <Route path="/book/:slug">
        <PublicBookingPage />
      </Route>

      <Route path="/booking/:manageToken">
        <BookingManage />
      </Route>

      <Route path="/upload/:token">
        <UploadPage />
      </Route>

      <Route path="/connect-bank">
        <ConnectBank />
      </Route>

      <Route path="/login">
        {user ? <Redirect to="/" /> : <Login />}
      </Route>

      <Route path="/forgot-password">
        {user ? <Redirect to="/" /> : <ForgotPassword />}
      </Route>

      <Route path="/auth/callback">
        <AuthCallback />
      </Route>

      <Route path="/set-password">
        <SetPassword />
      </Route>

      {/* 🔐 Protected routes */}
      <Route path="/">
        <AuthGuard><Projects /></AuthGuard>
      </Route>

      <Route path="/today">
        <AuthGuard><Today /></AuthGuard>
      </Route>

      <Route path="/settings">
        <AuthGuard><Settings /></AuthGuard>
      </Route>

      <Route path="/reports">
        <AuthGuard><Reports /></AuthGuard>
      </Route>

      <Route path="/period">
        <AuthGuard><ThisPayPeriod /></AuthGuard>
      </Route>

      <Route path="/submit">
        <AuthGuard><Submit /></AuthGuard>
      </Route>

      <Route path="/history">
        <AuthGuard><PlaceholderPage title="History" /></AuthGuard>
      </Route>

      <Route path="/review">
        <AuthGuard><Review /></AuthGuard>
      </Route>

      <Route path="/accounts">
        <AuthGuard><Accounts /></AuthGuard>
      </Route>

      <Route path="/messages">
        <AuthGuard><Messages /></AuthGuard>
      </Route>

      <Route path="/accounts/:id">
        <AuthGuard><AccountDetail /></AuthGuard>
      </Route>

      <Route path="/inbox">
        <AuthGuard><Inbox /></AuthGuard>
      </Route>
      <Route path="/email-search">
        <AuthGuard><EmailSearch /></AuthGuard>
      </Route>
      <Route path="/my-accounts">
        <AuthGuard><MyAccounts /></AuthGuard>
      </Route>
      {/* Legacy URL — "My Book" was renamed "My Accounts" (v1.5.63); keep old bookmarks working. */}
      <Route path="/my-book">
        <Redirect to="/my-accounts" />
      </Route>
      <Route path="/client-responses">
        <AuthGuard><ClientResponses /></AuthGuard>
      </Route>
      <Route path="/client-email-log">
        <AuthGuard><ClientEmailLog /></AuthGuard>
      </Route>

      <Route path="/sources">
        <AuthGuard><FinancialSources /></AuthGuard>
      </Route>

      <Route path="/contacts">
        <AuthGuard><Contacts /></AuthGuard>
      </Route>

      <Route path="/client-groups">
        <AuthGuard><ClientGroups /></AuthGuard>
      </Route>

      <Route path="/engagements">
        <AuthGuard><PlaceholderPage title="Engagements" /></AuthGuard>
      </Route>

      <Route path="/projects">
        <AuthGuard><Redirect to="/" /></AuthGuard>
      </Route>

      <Route path="/tasks">
        <AuthGuard><Tasks /></AuthGuard>
      </Route>

      <Route path="/templates">
        <AuthGuard><Templates /></AuthGuard>
      </Route>

      <Route path="/templates/:id">
        <AuthGuard><TemplateEdit /></AuthGuard>
      </Route>

      <Route path="/invoices">
        <AuthGuard><Layout><Invoices /></Layout></AuthGuard>
      </Route>

      <Route path="/invoices/:id">
        <AuthGuard><Layout><InvoiceEdit /></Layout></AuthGuard>
      </Route>

      <Route path="/invoices/:id/edit">
        <AuthGuard><Layout><InvoiceEdit /></Layout></AuthGuard>
      </Route>

      <Route path="/exports">
        <AuthGuard><PlaceholderPage title="Exports" /></AuthGuard>
      </Route>

      <Route path="/codes">
        <AuthGuard><PlaceholderPage title="Activity Codes" /></AuthGuard>
      </Route>

      <Route path="/tags">
        <AuthGuard><TagsPage /></AuthGuard>
      </Route>

      <Route path="/pto">
        <AuthGuard><PTO /></AuthGuard>
      </Route>

      <Route path="/periods">
        <AuthGuard><PayPeriodAdmin /></AuthGuard>
      </Route>

      <Route path="/settings/invite">
        <AuthGuard><InviteTeam /></AuthGuard>
      </Route>

      <Route path="/settings/admin">
        <AuthGuard><AdminConfig /></AuthGuard>
      </Route>

      <Route path="/settings/automation-rules">
        <AuthGuard><AutomationRules /></AuthGuard>
      </Route>

      <Route path="/users">
        <AuthGuard><UsersPage /></AuthGuard>
      </Route>

      <Route path="/admin/import">
        <AuthGuard><AdminImport /></AuthGuard>
      </Route>

      <Route path="/feedback">
        <AuthGuard><FeedbackPage /></AuthGuard>
      </Route>

      <Route path="/notifications">
        <AuthGuard><NotificationsPage /></AuthGuard>
      </Route>

      <Route path="/trash">
        <AuthGuard><TrashPage /></AuthGuard>
      </Route>

      <Route path="/timesheets/all">
        <AuthGuard><AllTimesheets /></AuthGuard>
      </Route>

      <Route path="/reports/time">
        <AuthGuard><ReportTime /></AuthGuard>
      </Route>

      <Route path="/reports/capacity">
        <AuthGuard><ReportCapacity /></AuthGuard>
      </Route>

      <Route path="/calendar">
        <AuthGuard><CalendarPage /></AuthGuard>
      </Route>

      <Route path="/scheduling">
        <AuthGuard><SchedulingSettings /></AuthGuard>
      </Route>

      <Route path="/signatures">
        <AuthGuard><Signatures /></AuthGuard>
      </Route>

      <Route path="/admin/agreements">
        <AuthGuard><AgreementTemplates /></AuthGuard>
      </Route>

      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <ConfirmProvider>
      <AuthProvider>
        <TimerProvider>
          <QuickCreateProvider>
            <AIPageContextProvider>
              <AppRoutes />
              <Toaster />
            </AIPageContextProvider>
          </QuickCreateProvider>
        </TimerProvider>
      </AuthProvider>
    </ConfirmProvider>
  );
}

export default App;
